import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

// Persists a list page's search / filters / page in the URL query string so views
// are deep-linkable and survive back/forward navigation — the console standard
// (docs/admin-platform-operations-console-design.md §UX). Filter keys are arbitrary;
// `q` and `page` are conventional.
export interface UrlTableState {
  q: string;
  page: number;
  filters: Record<string, string>;
  setQ: (v: string) => void;
  setPage: (p: number) => void;
  setFilter: (key: string, value: string | undefined) => void;
  reset: () => void;
}

const RESERVED = new Set(['q', 'page']);

export function useUrlTableState(filterKeys: string[] = []): UrlTableState {
  const [params, setParams] = useSearchParams();

  const q = params.get('q') ?? '';
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  const filters = useMemo(() => {
    const out: Record<string, string> = {};
    for (const k of filterKeys) {
      const v = params.get(k);
      if (v) out[k] = v;
    }
    return out;
  }, [params, filterKeys.join(',')]);

  const patch = useCallback(
    (mut: (next: URLSearchParams) => void) => {
      const next = new URLSearchParams(params);
      mut(next);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const setQ = useCallback((v: string) => patch((n) => {
    if (v) n.set('q', v); else n.delete('q');
    n.delete('page'); // new search resets to page 1
  }), [patch]);

  const setPage = useCallback((p: number) => patch((n) => {
    if (p > 1) n.set('page', String(p)); else n.delete('page');
  }), [patch]);

  const setFilter = useCallback((key: string, value: string | undefined) => patch((n) => {
    if (value) n.set(key, value); else n.delete(key);
    n.delete('page');
  }), [patch]);

  const reset = useCallback(() => patch((n) => {
    for (const k of [...RESERVED, ...filterKeys]) n.delete(k);
  }), [patch, filterKeys.join(',')]);

  return { q, page, filters, setQ, setPage, setFilter, reset };
}
